import { DeleteOutlined, SettingOutlined } from '@ant-design/icons'
import { Link, useNavigate } from '@tanstack/react-router'
import { Badge, Button, Flex, Pagination, PaginationProps, Space, Tooltip, Typography } from 'antd'
import React, { useState } from 'react'
import { Campaign } from '../campaign'
import { CampaignDeleteModal } from './campaign-delete-modal'
import { useTranslation } from 'react-i18next'
import { TimeAgo } from '../../components/time-ago'
import { useAuth } from '../../auth'
import { UPDATE_CAMPAIGN } from '../../user/permission'
import { ListContainer } from '../../components/list-container'
import { ACTIVE } from '../status'

export type CampaignListProps = {
    campaigns: Campaign[]
    pagination: PaginationProps
}

export function CampaignList({ campaigns, pagination }: CampaignListProps) {
    const { t } = useTranslation()

    return (
        <Flex
            vertical
            gap="middle"
        >
            <ListContainer
                isEmpty={campaigns.length === 0}
                emptyText={t('No campaigns found')}
            >
                {campaigns.map((campaign) => (
                    <CampaignRow
                        key={campaign.id}
                        campaign={campaign}
                    />
                ))}
            </ListContainer>

            {(pagination?.total || 0) > 10 && (
                <Flex justify="end">
                    <Pagination {...pagination} />
                </Flex>
            )}
        </Flex>
    )
}

type CampaignRowProps = {
    campaign: Campaign
}

function CampaignRow({ campaign }: CampaignRowProps) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const navigate = useNavigate()
    const [isDeleteOpen, setDeleteOpen] = useState(false)

    const isActive = campaign.status === ACTIVE
    const campaignId = campaign.id.toString()

    const openBoard = () => {
        navigate({
            to: '/campaigns/$campaignId/board',
            params: { campaignId },
        })
    }

    const stop = (event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.currentTarget !== event.target || (event.key !== 'Enter' && event.key !== ' ')) {
            return
        }

        event.preventDefault()
        openBoard()
    }

    return (
        <>
            <div
                role="link"
                tabIndex={0}
                onClick={openBoard}
                onKeyDown={onKeyDown}
                className="block cursor-pointer border-b border-divider transition-colors last:border-b-0 hover:bg-surface-hover"
            >
                <Flex
                    gap="middle"
                    justify="space-between"
                    align="flex-start"
                    className="px-4 py-4"
                >
                    <Flex
                        vertical
                        gap={9}
                        className="min-w-0"
                    >
                        <Flex
                            align="center"
                            gap={8}
                            wrap="wrap"
                        >
                            <Typography.Text
                                strong
                                className="text-sm text-primary"
                            >
                                <Link
                                    to="/campaigns/$campaignId/board"
                                    params={{ campaignId }}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                    }}
                                >
                                    {campaign.title}
                                </Link>
                            </Typography.Text>
                            <Tooltip title={isActive ? t('Conversations active') : t('Conversations inactive')}>
                                <Badge
                                    status={isActive ? 'success' : 'default'}
                                    text={isActive ? t('Active') : t('Inactive')}
                                />
                            </Tooltip>
                        </Flex>

                        <Flex
                            gap={6}
                            align="center"
                            wrap="wrap"
                            className="text-xs text-ink-tertiary"
                        >
                            {campaign.company && (
                                <span className="font-semibold text-ink-secondary">{campaign.company.name} ·</span>
                            )}
                            <TimeAgo date={campaign.createdAt} />
                        </Flex>
                    </Flex>

                    <Space
                        size={4}
                        className="shrink-0"
                        onClick={stop}
                    >
                        <Link
                            to="/campaigns/$campaignId/settings"
                            params={{ campaignId }}
                            disabled={!can(UPDATE_CAMPAIGN)}
                        >
                            <Button
                                size="small"
                                type="text"
                                icon={<SettingOutlined />}
                                disabled={!can(UPDATE_CAMPAIGN)}
                                aria-label={t('Settings')}
                            />
                        </Link>
                        <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            disabled // @todo implement delete
                            onClick={(event) => {
                                stop(event)
                                setDeleteOpen(true)
                            }}
                            aria-label={t('Delete')}
                        />
                    </Space>
                </Flex>
            </div>
            <CampaignDeleteModal
                open={isDeleteOpen}
                close={() => {
                    setDeleteOpen(false)
                }}
                campaign={campaign}
            />
        </>
    )
}
